import React, { useMemo, useState, useRef, useEffect } from "react";
import { ChevronDown, FileText, Check } from "lucide-react";
import { useSiteContext } from "../contexts/SiteContext";
import { useMCPContext } from "../hooks/useMCPContext";

const ACCENT = "var(--accent, #8B5CF6)";

const formatReportLabel = (scoredAt?: string | null, createdAt?: string | null) => {
  const raw = scoredAt || createdAt;
  if (!raw) return "Undated report";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "Undated report";
  return date.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ReportSwitcher: React.FC = () => {
  const { activeSite, selectedReportId, selectReport } = useSiteContext();
  const domain = activeSite?.domain || "";
  const { overview } = useMCPContext({ domain, reportId: selectedReportId });
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const reports = overview?.availableReports || [];
  const activeReportId = selectedReportId || overview?.reportId || "";

  const options = useMemo(
    () =>
      reports.map((report) => ({
        id: report.reportId,
        title: formatReportLabel(report.scoredAt, report.createdAt),
        subtitle: [
          report.compositeScore != null ? `Score ${report.compositeScore}` : null,
          report.complete === false ? "Incomplete" : null,
        ].filter(Boolean).join(" · ") || null,
      })),
    [reports]
  );

  const activeOption = options.find((option) => option.id === activeReportId);

  useEffect(() => {
    if (domain && !selectedReportId && overview?.reportId) {
      selectReport(overview.reportId);
    }
  }, [domain, selectedReportId, overview?.reportId, selectReport]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!domain || options.length === 0) return null;

  return (
    <div className="relative w-fit" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="inline-flex max-w-[260px] items-center gap-2 rounded-[10px] border border-white/[0.07] px-3 py-2 transition-colors hover:border-white/[0.12]"
        style={{ background: isOpen ? "#0F0F18" : "transparent" }}
      >
        <FileText className="h-4 w-4 flex-shrink-0" style={{ color: ACCENT }} />
        <span className="truncate text-sm font-medium text-og01-text">
          {activeOption?.title || "Select a report"}
        </span>
        <ChevronDown
          className="h-3.5 w-3.5 flex-shrink-0 text-og01-text-muted transition-transform"
          style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
        />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full z-50 mt-2 w-max min-w-full overflow-hidden rounded-xl border border-white/[0.08]"
          style={{
            background: "rgba(15,15,24,0.98)",
            boxShadow: "0 24px 60px -20px rgba(0,0,0,.7)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div className="px-4 pb-2 pt-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-og01-text-muted">Reports</div>
          </div>

          <div className="max-h-[280px] overflow-y-auto px-2 pb-2" role="listbox" aria-label="Saved reports">
            {options.map((option) => {
              const isActive = option.id === activeReportId;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    selectReport(option.id);
                    setIsOpen(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/5"
                  style={{ background: isActive ? `color-mix(in srgb, ${ACCENT} 10%, transparent)` : "transparent" }}
                >
                  <div
                    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border"
                    style={{
                      background: isActive ? `color-mix(in srgb, ${ACCENT} 18%, transparent)` : "rgba(255,255,255,0.04)",
                      borderColor: isActive ? `color-mix(in srgb, ${ACCENT} 32%, transparent)` : "rgba(255,255,255,0.07)",
                    }}
                  >
                    {isActive ? (
                      <Check className="h-3.5 w-3.5" style={{ color: ACCENT }} />
                    ) : (
                      <FileText className="h-3.5 w-3.5 text-og01-text-muted" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className={`truncate text-sm font-medium ${isActive ? "text-og01-text" : "text-og01-text-secondary"}`}>
                      {option.title}
                    </div>
                    {option.subtitle && (
                      <div className="text-[10px] text-og01-text-muted">{option.subtitle}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="border-t border-white/[0.07] px-4 py-2 text-center">
            <span className="text-[10px] text-og01-text-muted">
              {options.length} report{options.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportSwitcher;

